import { ReactNode, useMemo } from "react";

import { ExperienceTestTypeDto } from "@intelligems/ig-types/dtos/experiences";
import { GenericExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { PostPurchaseSetupStartMessages } from "@intelligems/ig-utils/post-purchase-setup";
import { useConfiguration } from "@src/api/configurations/use-configuration";

import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { usePostPurchaseSetupIssue } from "@src/components/LimitAccess/hooks";

import { PostPurchaseAudienceOverlapMessage } from "./PostPurchaseAudienceOverlapMessage";
import { CartTransformConflictMessage } from "./CartTransformConflictMessage";

import { useIsPriceTestIntegrationReady } from "./useIsPriceTestIntegrationReady";
import { useIsShippingTestAlreadyActive } from "./useIsShippingTestAlreadyActive";
import { useIsCartTransformTestConflictActive } from "./useIsCartTransformTestConflictActive";
import { useHasLivePostPurchaseWithSameAudience } from "./useHasLivePostPurchaseWithSameAudience";
import { useIsTestLimitHit } from "./useIsTestLimitHit";
import { experimentScopeMaps } from "./experimentScopeMaps";
const { StartTestTypeMap, FriendlyTestTypeMap } = experimentScopeMaps;

export const useCanStartExperience = (
  experience: GenericExperienceFe
): { canStart: boolean; disabledMessage: ReactNode } => {
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const scopeMap = useBooleanWebUiScopeMap();
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const isShippingTestAlreadyActive = useIsShippingTestAlreadyActive();
  const isPricingCartTransformConflict = useIsCartTransformTestConflictActive(
    experience.id,
    "hasTestPricing",
    Boolean(experience.testTypes.hasTestPricing)
  );
  const isShippingCartTransformConflict = useIsCartTransformTestConflictActive(
    experience.id,
    "hasTestShipping",
    Boolean(experience.testTypes.hasTestShipping)
  );
  const isTestLimitHit = useIsTestLimitHit();
  const hasLivePostPurchaseWithSameAudience =
    useHasLivePostPurchaseWithSameAudience(experience);
  const isPostPurchaseCheckoutEnabled = useConfiguration(
    "enable_post_purchase_checkout"
  )?.value;
  const postPurchaseSetupIssue = usePostPurchaseSetupIssue();
  const isAdmin = useIsAdmin();

  return useMemo(() => {
    const category =
      experience.category === "experiment" ? "test" : "personalization";

    if (!experience) {
      return { canStart: false, disabledMessage: "" };
    }

    if (!isSubscriptionActive) {
      return {
        canStart: false,
        disabledMessage: `Approve billing to start this ${category}`,
      };
    }

    const missingTestTypes = Object.entries(experience.testTypes)
      .map(([key, value]) => {
        if (value) {
          let scopeKey = StartTestTypeMap[key as keyof ExperienceTestTypeDto];

          if (
            key === "hasTestCampaign" &&
            experience.category === "personalization"
          ) {
            scopeKey = "start_offer";
          }

          const result = scopeMap[scopeKey];
          if (!result) {
            return FriendlyTestTypeMap[key as keyof ExperienceTestTypeDto];
          }
        }
        return null;
      })
      .filter((s): s is string => Boolean(s));

    const hasNecessaryScopes = missingTestTypes.length === 0;

    // Admins bypass plan-derived limits (concurrent-test limit and plan
    // scopes) but still hit the operational checks below.
    if (isTestLimitHit && !isAdmin) {
      return {
        canStart: false,
        disabledMessage: `You have reached the maximum number of active tests. End an existing test to start a new one.`,
      };
    }

    if (!hasNecessaryScopes && !isAdmin) {
      return {
        canStart: false,
        disabledMessage: `Upgrade your plan to start this ${missingTestTypes.join(", ")} ${category}`,
      };
    }

    if (experience.testTypes.hasTestPricing) {
      if (!isPriceTestIntegrationReady) {
        return {
          canStart: false,
          disabledMessage: `Complete your pricing integration to start this ${category}`,
        };
      }
      if (isPricingCartTransformConflict && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: CartTransformConflictMessage("Shipping Test"),
        };
      }
    }

    if (experience.testTypes.hasTestShipping) {
      if (isShippingTestAlreadyActive && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: `Only one shipping test can be run at a time. End your current shipping test to start a new one.`,
        };
      }
      if (isShippingCartTransformConflict && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: CartTransformConflictMessage("Price Test"),
        };
      }
    }

    if (experience.testTypes.hasTestPostPurchase) {
      if (!isPostPurchaseCheckoutEnabled && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: `Launching post purchase coming soon`,
        };
      }
      // Also covers starting from a table row, where the store's setup screen
      // never shows.
      if (postPurchaseSetupIssue && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage:
            PostPurchaseSetupStartMessages[postPurchaseSetupIssue],
        };
      }
      if (
        hasLivePostPurchaseWithSameAudience &&
        experience.status !== "started"
      ) {
        return {
          canStart: false,
          disabledMessage: PostPurchaseAudienceOverlapMessage,
        };
      }
    }

    return {
      canStart: true,
      disabledMessage: "",
    };
  }, [
    experience,
    isSubscriptionActive,
    scopeMap,
    isPriceTestIntegrationReady,
    isShippingTestAlreadyActive,
    isPricingCartTransformConflict,
    isShippingCartTransformConflict,
    isTestLimitHit,
    hasLivePostPurchaseWithSameAudience,
    isPostPurchaseCheckoutEnabled,
    postPurchaseSetupIssue,
    isAdmin,
  ]);
};
